import { type ComponentType, type ReactNode, type SVGProps } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../api/useAuth";
import { useTheme } from "../lib/useTheme";
import mohEmblem from "../assets/moh-emblem.webp";
import { JobWatcher } from "./JobWatcher";
import { SimJobTray, SimJobWatcher } from "./SimJobs";
import {
  ApprovalIcon,
  ContentGapsIcon,
  CoursesIcon,
  DashboardIcon,
  FailedIcon,
  LogoutIcon,
  MoonIcon,
  PipelineIcon,
  SimAttemptsIcon,
  SimPatientsIcon,
  SettingsIcon,
  SunIcon,
  TranscriptionsIcon,
} from "./icons";

// Nav items are listed here with the role required for each; items the
// current user can't use are hidden rather than shown-disabled, per the
// build spec (Issue #3). Extend this array as pages land — it's the one
// place nav visibility is decided.
const NAV_ITEMS: { to: string; label: string; icon: ComponentType<SVGProps<SVGSVGElement>>; roles?: string[] }[] = [
  { to: "/", label: "Dashboard", icon: DashboardIcon },
  { to: "/courses", label: "Courses", icon: CoursesIcon },
  { to: "/approvals", label: "Approval Queue", icon: ApprovalIcon },
  { to: "/runs", label: "Pipeline Runs", icon: PipelineIcon },
  { to: "/failed-extractions", label: "Failed Extractions", icon: FailedIcon },
  { to: "/transcriptions", label: "Transcriptions", icon: TranscriptionsIcon },
  { to: "/content-gaps", label: "Content Gaps", icon: ContentGapsIcon },
  { to: "/sim-patients", label: "Simulated Patients", icon: SimPatientsIcon },
  { to: "/sim-attempts", label: "Simulated Attempts", icon: SimAttemptsIcon },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();

  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      !item.roles || user?.role === "root" || item.roles.includes(user?.role ?? "")
  );

  const displayName = user?.full_name || user?.email || "—";
  const initials = (user?.full_name || user?.email || "?")
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");

  const isDark = theme === "dark";

  return (
    <div className="flex min-h-screen bg-canvas dark:bg-ink">
      <JobWatcher />
      <SimJobWatcher />
      <SimJobTray />
      <aside className="app-sidebar sticky top-0 flex h-screen w-64 shrink-0 flex-col border-r border-transparent bg-navy text-white dark:border-white/10 dark:bg-ink">
        <div className="flex shrink-0 items-center justify-center px-4 pb-1 pt-7">
          <img
            src={mohEmblem}
            alt="Ministry of Health, Ethiopia"
            className="h-[72px] w-[72px] rounded-full !bg-white object-contain p-1 ring-4 ring-accent/30 lg:h-20 lg:w-20 xl:h-24 xl:w-24"
          />
        </div>

        <nav className="mt-6 flex-1 space-y-1.5 overflow-y-auto px-3 pb-4">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-full px-4 py-2.5 text-sm transition-colors lg:text-base ${
                    isActive
                      ? "app-nav-active bg-accent font-semibold text-white shadow-sm"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        {/* Account row with theme switch and sign-out. */}
        <div className="shrink-0 border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-2 px-1 py-1.5">
            <span className="app-avatar grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-sm font-semibold text-white">
              {initials}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">{displayName}</span>
              <span className="block truncate text-xs capitalize text-white/50">{user?.role}</span>
            </span>
            <button
              type="button"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
              onClick={toggle}
              className="ml-auto shrink-0 rounded-full p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
            >
              {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
            </button>
            <button
              type="button"
              aria-label="Log out"
              title="Log out"
              onClick={() => void logout()}
              className="shrink-0 rounded-full p-1.5 text-white/80 hover:bg-white/10 hover:text-white"
            >
              <LogoutIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 bg-canvas dark:bg-ink">{children}</main>
    </div>
  );
}
